import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';

const AutocompleteInput = ({ 
  value, 
  onChange, 
  options, 
  placeholder, 
  className = "",
  allowCustom = true,
  onSelect,
  renderOption
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [inputValue, setInputValue] = useState(value || '');
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (inputValue) {
      const filtered = options.filter(option => {
        const searchText = typeof option === 'string' ? option : option.name;
        return searchText.toLowerCase().includes(inputValue.toLowerCase());
      });
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [inputValue, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleOptionSelect = (option) => {
    const selectedValue = typeof option === 'string' ? option : option.name;
    setInputValue(selectedValue);
    setIsOpen(false);
    
    if (onChange) {
      onChange(selectedValue);
    }
    
    if (onSelect) {
      onSelect(option);
    }
  };

  const handleClear = () => {
    setInputValue('');
    setIsOpen(false);
    
    if (onChange) {
      onChange('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`input-field pr-8 ${className}`}
          autoComplete="off"
        />
        
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
          {inputValue && (
            <button
              onClick={handleClear}
              className="p-1 text-gray-400 hover:text-gray-600"
              type="button"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <ChevronDown 
            className={`h-4 w-4 text-gray-400 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`} 
          />
        </div>
      </div>

      {isOpen && filteredOptions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.map((option, index) => {
            const optionValue = typeof option === 'string' ? option : option.name;
            const isSelected = optionValue === inputValue;
            
            return (
              <button
                key={index}
                onClick={() => handleOptionSelect(option)}
                className={`w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors ${
                  isSelected ? 'bg-primary-50 text-primary-600' : 'text-gray-900'
                }`}
                type="button"
              >
                {renderOption ? renderOption(option) : optionValue}
              </button>
            );
          })}
          
          {allowCustom && inputValue && !filteredOptions.some(option => {
            const optionValue = typeof option === 'string' ? option : option.name;
            return optionValue.toLowerCase() === inputValue.toLowerCase();
          }) && (
            <button
              onClick={() => handleOptionSelect(inputValue)}
              className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors text-primary-600 border-t border-gray-200"
              type="button"
            >
              Add "{inputValue}"
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;
